const PropTypes = require('prop-types');
const React = require('react');
const EditableAddress = require('./editable_address');
const UrlHelpers = require('components/shared/url_helpers.js');
const UiUtil = require('components/utils/ui.js');
const AddressLoadingPlaceholder = require('./address_loading_placeholder.jsx');
const Pagination = require('components/shared/pagination.jsx');
const firstPage = 1;
const { MobileDetection } = require('@reverbdotcom/discovery-ui');

class AddressList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      addresses: [],
      countriesForSelect: [],
      addingNewAddress: false,
      initialSelectedAddressId: props.selectedAddressId,
      formAddressErrors: props.formAddressErrors,
      nowEditingAddressId: props.formAddressErrors != null ? props.selectedAddressId : null,
      totalPages: 0,
      currentPage: null,
    };
  }

  componentDidMount() {
    this.fetchCountriesForSelect();
    this.fetchAddresses(firstPage);
  }

  handleFormSave() {
    this.setState({
      initialSelectedAddressId: null,
      formAddressErrors: null,
    });
    if (MobileDetection.isMobileScreenWidth()) {
      UiUtil.scrollToElement(this.refs.addressListRoot);
    }
    this.fetchAddresses(firstPage);
  }

  fetchAddresses(page) {
    Reverb.APIRequest.V3.get(
      UrlHelpers.myAddressesIndexPath, { per_page: this.props.perPage, page }
    ).done((response) => {
      this.setState({
        addresses: response.addresses,
        addingNewAddress: false,
        totalPages: response.total_pages,
        currentPage: response.current_page,
      });
    });
  }

  hasPagination() {
    return this.state.totalPages > 1;
  }

  fetchCountriesForSelect() {
    Reverb.APIRequest.V3.get(
      UrlHelpers.countriesPath
    ).done((response) => {
      this.setState({
        countriesForSelect: response.countries,
      });
    });
  }

  isLoading() {
    // either the countries list or the first page of addresses have not yet loaded from the API
    return (this.state.countriesForSelect.length === 0 || !this.state.currentPage);
  }

  isSelected(address, index) {
    if (this.state.initialSelectedAddressId && this.selectedAddress()) {
      return address.id === this.state.initialSelectedAddressId;
    }

    // When creating a new address, the new address form is selected,
    // and none of the persisted addresses are selected.
    if (this.isCreatingAddress()) {
      return false;
    }

    return index === 0;
  }

  formErrorForAddress(address) {
    if (address.id === this.state.initialSelectedAddressId) {
      return this.state.formAddressErrors;
    }
    return null;
  }

  isEditing(address) {
    return address.id === this.state.nowEditingAddressId;
  }

  selectedAddress() {
    return this.state.addresses.find((address) =>
      address.id === this.state.initialSelectedAddressId
    );
  }

  nonSelectedAddresses() {
    return this.state.addresses.filter((address) =>
      address.id !== this.state.initialSelectedAddressId
    );
  }

  addressesInOrder() {
    const addressList = this.nonSelectedAddresses().slice(0);
    const selectedAddress = this.selectedAddress();
    if (selectedAddress) {
      addressList.unshift(selectedAddress);
    }
    return addressList;
  }

  // The key for an EditableAddress is based on the address AND
  // whether the new address form is open, so if the user clicks
  // 'Cancel' in the new address form the EditableAddress for
  // their primary address gets re-rendered in selected state.
  keyForEditableAddress(address, index) {
    return `${address.id}-${this.isCreatingAddress()}-${this.isSelected(address, index)}`;
  }

  persistedAddresses() {
    return (
      this.addressesInOrder().map((address, index) =>
        <EditableAddress
          key={this.keyForEditableAddress(address, index)}
          name={address.name}
          addressId={address.id}
          streetAddress={address.street_address}
          extendedAddress={address.extended_address}
          locality={address.locality}
          region={address.region}
          postalCode={address.postal_code}
          countryCode={address.country_code}
          phone={address.phone}
          handleUpdate={() => this.handleFormSave()}
          countriesForSelect={this.state.countriesForSelect}
          selectable={this.props.selectable}
          selected={this.isSelected(address, index)}
          formObjectName={this.props.formObjectName}
          editing={this.isEditing(address)}
          formErrors={this.formErrorForAddress(address)}
          closeNewAddressForm={() => {
            this.setState({ addingNewAddress: false });
          }}
          handleEditing={(addressId) => {
            this.setState({ nowEditingAddressId: addressId, addingNewAddress: false });
          }}
          displayCancelEditLink={this.displayCancelEditLink()}
        />
      )
    );
  }

  blankAddressForm() {
    if (this.isCreatingAddress()) {
      return (
        <EditableAddress
          countriesForSelect={this.state.countriesForSelect}
          handleUpdate={() => this.handleFormSave()}
          selectable={this.props.selectable}
          formObjectName={this.props.formObjectName}
          handleEditing={(addressId) => this.setState({ nowEditingAddressId: addressId })}
          displayCancelEditLink={this.displayCancelEditLink()}
          countryCodeDefault={this.props.countryCodeDefault}
          selected
          editing
        />
      );
    }
    return null;
  }

  displayCancelEditLink() {
    if (this.props.selectable) {
      return this.hasAddresses();
    }
    return true;
  }

  hasAddresses() {
    return this.addressCount() > 0;
  }

  addressCount() {
    return this.state.addresses.length;
  }

  isCreatingAddress() {
    if (this.props.selectable) {
      return !this.hasAddresses() || this.state.addingNewAddress;
    }
    return this.state.addingNewAddress;
  }

  handleAddNewAddressClick() {
    this.setState({
      addingNewAddress: true,
      nowEditingAddressId: null,
    });
  }

  addNewAddressButton() {
    if (this.isCreatingAddress()) { return null; }

    if (this.props.selectable || this.hasAddresses()) {
      return (
        <a
          id="add_new_address"
          className="btn btn-blue wide-mobile"
          onClick={() => this.handleAddNewAddressClick()}
        >
          {I18n.t('js.addresses.addressList.addNewAddress')}
        </a>
      );
    }
    if (!this.props.selectable && !this.hasAddresses()) {
      return (
        <div className="align-center">
          <p className="mtb-1 muted">
            {I18n.t('js.addresses.addressList.noAddressesOnFile')}
          </p>
          <a
            id="add_new_address"
            className="btn btn-primary"
            onClick={() => this.handleAddNewAddressClick()}
          >
            {I18n.t('js.addresses.addressList.addAddress')}
          </a>
        </div>
      );
    }
    return null;
  }

  renderPagination() {
    if (this.hasPagination()) {
      return (
        <Pagination
          changePage={(page) => this.fetchAddresses(page)}
          totalPages={this.state.totalPages}
          currentPage={this.state.currentPage}
        />
      );
    }

    return null;
  }

  render() {
    if (this.isLoading()) return <AddressLoadingPlaceholder />;
    return (
      <div ref="addressListRoot">
        <ul>
          {this.persistedAddresses()}
          {this.blankAddressForm()}
        </ul>
        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12 mobile-mb-1">
            {this.addNewAddressButton()}
          </div>
          <div className="g-col-6 g-col-mobile-12 checkout-address-pagination">
            {this.renderPagination()}
          </div>
        </div>
      </div>
    );
  }
}

AddressList.propTypes = {
  formObjectName: PropTypes.string,
  selectable: PropTypes.bool.isRequired,
  selectedAddressId: PropTypes.string,
  countryCodeDefault: PropTypes.string,
  perPage: PropTypes.number.isRequired,
  formAddressErrors: PropTypes.string,
};

module.exports = AddressList;



// WEBPACK FOOTER //
// ./javascripts/components/addresses/address_list.jsx